/**
 * Shared case-study data used by both /case-studies and /case-studies/$slug.
 * Anonymized (industry + city) to respect client NDAs while still giving
 * Google and AI engines real, dated, vertical-specific E-E-A-T material.
 */

export type CaseStudy = {
  slug: string;
  industry: string;
  location: string;
  service: string[];
  /** Short challenge — 1 sentence, used on the grid card (italic). */
  challenge: string;
  /** Big bold stat shown on the card and as the lead metric on the detail page. */
  heroResult: string;
  /** Short quote used on the card (also reused as the full quote on the detail page if no longer one is provided). */
  cardQuote: string;
  headline: string;
  summary: string;
  problem: string;
  approach: string[];
  results: { label: string; value: string; context?: string }[];
  quote?: { text: string; attribution: string };
  /** ISO date — used for Article schema datePublished. */
  datePublished: string;
};

export const CASE_STUDIES: CaseStudy[] = [
  {
    slug: "hvac-colorado-springs",
    industry: "HVAC",
    location: "Colorado Springs",
    service: ["SEO", "Web Design", "Google Maps"],
    challenge:
      "Phones were silent outside peak season despite years in business.",
    heroResult: "10× monthly leads in 6 months",
    cardQuote:
      "Our phone hasn't stopped ringing since they rebuilt our site and dialed in our SEO. Best marketing investment we've ever made.",
    headline:
      "HVAC Company in Colorado Springs — 10× Monthly Leads in 6 Months",
    summary:
      "A Colorado Springs HVAC company replaced a stale website and underperforming SEO — and grew monthly inbound leads by roughly 10× within six months.",
    problem:
      "A residential HVAC company in Colorado Springs had years of experience and a great reputation, but their phones went quiet the moment peak season ended. Their website was slow, mobile-broken, and almost invisible in Google Maps. Off-season cash flow was a real problem.",
    approach: [
      "Hand-coded a fast, mobile-first website with click-to-call in the header and a sticky CTA bar.",
      "Rebuilt the Google Business Profile from scratch — categories, services, service-area polygons, and fresh photos.",
      "Launched targeted SEO around year-round HVAC searches (tune-ups, IAQ, repair) — not just summer/winter peaks.",
      "Built dedicated service-area pages for Colorado Springs, Monument, Falcon, Fountain, and Security-Widefield.",
      "Set up a structured review-request workflow tied to job completion.",
    ],
    results: [
      { label: "Monthly leads", value: "≈ 10×", context: "vs. trailing 6 months" },
      { label: "Off-season calls", value: "Steady year-round", context: "no more summer-only spikes" },
      { label: "Mobile site speed", value: "Significantly faster", context: "vs. previous build" },
      { label: "Map Pack visibility", value: "Top 3", context: "for primary Springs HVAC terms" },
    ],
    quote: {
      text: "Our phone hasn't stopped ringing since they rebuilt our site and dialed in our SEO. Best marketing investment we've ever made.",
      attribution: "Owner, Colorado Springs HVAC company",
    },
    datePublished: "2024-09-15",
  },
  {
    slug: "plumbing-monument-co",
    industry: "Plumbing",
    location: "Monument, CO",
    service: ["Google Maps Optimization", "Local SEO"],
    challenge:
      "Stuck on page 4 of Google with almost no Map Pack visibility.",
    heroResult: "Page 4 → Top 3 Map Pack in 5 months",
    cardQuote:
      "We went from page 4 to the top of the Map Pack in under 5 months. They truly understand the Colorado market.",
    headline:
      "Plumbing Company in Monument — Page 4 to Top 3 Map Pack in 5 Months",
    summary:
      "A Monument-based plumbing company moved from page 4 of Google to the top 3 of the Map Pack across their core service terms in under five months.",
    problem:
      "A small plumbing company in Monument was buried on page 4 of Google for almost every term that mattered. Their Google Business Profile was unverified for the right service area, citations were inconsistent, and they were getting almost zero map-driven calls.",
    approach: [
      "Verified and rebuilt the Google Business Profile with correct categories, services, and service-area polygons.",
      "Cleaned up NAP inconsistencies across the top local citation sources in Colorado.",
      "Wrote real, location-specific content for Monument, Tri-Lakes, Palmer Lake, and Black Forest.",
      "Added LocalBusiness, Service, and Review schema with proper @id graph linkage.",
      "Built a sustainable Google review request workflow tied to invoicing.",
    ],
    results: [
      { label: "Map Pack position", value: "Top 3", context: "for core Monument plumbing terms" },
      { label: "Organic ranking", value: "Page 4 → Page 1", context: "in 5 months" },
      { label: "Map-driven calls", value: "Major increase", context: "vs. baseline" },
      { label: "Review velocity", value: "Steady monthly cadence", context: "up from near-zero" },
    ],
    quote: {
      text: "We went from page 4 to the top of the Map Pack in under 5 months. They truly understand the Colorado market.",
      attribution: "Owner, Monument plumbing company",
    },
    datePublished: "2024-10-20",
  },
  {
    slug: "roofing-denver",
    industry: "Roofing",
    location: "Denver",
    service: ["PPC / Google Ads", "Landing Pages", "Conversion Tracking"],
    challenge:
      "Google Ads campaigns bleeding money with no ROI tracking.",
    heroResult: "12× return on ad spend in Q1",
    cardQuote:
      "Other agencies talked. They actually delivered. 12× return on our Google Ads in the first quarter.",
    headline: "Roofing Company in Denver — 12× Return on Ad Spend in Q1",
    summary:
      "A Denver roofing contractor went from a leaking, untracked Google Ads account to a 12× return on ad spend in a single quarter after a full PPC and landing-page rebuild.",
    problem:
      "A Denver roofing company was spending heavily on Google Ads with almost no conversion tracking. Their previous agency couldn't tell them which campaigns produced leads, the landing pages were generic, and the cost per lead was rising every month.",
    approach: [
      "Rebuilt Google Ads campaigns from scratch — proper match types, negatives, geo-targets, and ad groups.",
      "Implemented full conversion tracking (calls, forms, and offline lead status) tied back to ad spend.",
      "Built dedicated, fast-loading landing pages for roof replacement, storm damage, and insurance claims.",
      "Layered remarketing and call-only campaigns for high-intent storm-season queries.",
      "Set up a transparent monthly ROAS dashboard the owner could read in 60 seconds.",
    ],
    results: [
      { label: "Return on ad spend", value: "12×", context: "in Q1" },
      { label: "Cost per lead", value: "Substantially lower", context: "vs. previous agency" },
      { label: "Conversion tracking", value: "Fully attributed", context: "calls + forms + offline status" },
      { label: "Wasted spend", value: "Eliminated", context: "via negatives + match-type cleanup" },
    ],
    quote: {
      text: "Other agencies talked. They actually delivered. 12× return on our Google Ads in the first quarter.",
      attribution: "Owner, Denver roofing company",
    },
    datePublished: "2025-01-18",
  },
  {
    slug: "landscaping-castle-rock",
    industry: "Landscaping",
    location: "Castle Rock",
    service: ["Web Design", "SEO", "Lead Forms"],
    challenge: "Website was a brochure — generating zero leads.",
    heroResult: "0 → 30+ qualified leads per month",
    cardQuote:
      "Transparent, fast, and they actually pick up the phone. Our website is finally a real sales asset.",
    headline:
      "Landscaping Company in Castle Rock — 0 to 30+ Qualified Leads Per Month",
    summary:
      "A Castle Rock landscaping company turned a static brochure website into a true sales asset, going from zero inbound leads to 30+ qualified leads per month.",
    problem:
      "A Castle Rock landscaping company had a pretty website that did nothing. No clear CTAs, no lead forms that worked on mobile, no SEO foundation, and almost no inbound contacts. The owner was relying entirely on word of mouth and door-knocking.",
    approach: [
      "Rebuilt the site with a fast, mobile-first design and clear CTAs on every page.",
      "Added simple, conversion-tested lead forms with project-type qualifiers.",
      "Built service pages for design/build, maintenance, hardscaping, and irrigation.",
      "Targeted SEO around Castle Rock, Castle Pines, Parker, and Highlands Ranch landscaping searches.",
      "Set up clear lead routing to the owner's phone with daily summary emails.",
    ],
    results: [
      { label: "Qualified leads / month", value: "0 → 30+", context: "after launch" },
      { label: "Form submissions", value: "Consistent weekly volume", context: "across desktop + mobile" },
      { label: "Local rankings", value: "Page 1", context: "for Castle Rock landscaping terms" },
      { label: "Owner time on marketing", value: "Drastically reduced", context: "leads come to them" },
    ],
    quote: {
      text: "Transparent, fast, and they actually pick up the phone. Our website is finally a real sales asset.",
      attribution: "Owner, Castle Rock landscaping company",
    },
    datePublished: "2025-03-05",
  },
  {
    slug: "electrical-pueblo",
    industry: "Electrical",
    location: "Pueblo",
    service: [
      "Full-Service Digital Marketing",
      "Local SEO",
      "GBP Optimization",
    ],
    challenge:
      "Inconsistent lead flow — couldn't justify hiring another tech.",
    heroResult: "Fully booked — hired 2 additional technicians",
    cardQuote:
      "Booked solid through the busy season. We've had to hire two more techs to keep up with the leads.",
    headline:
      "Electrical Contractor in Pueblo — Fully Booked, Hired 2 More Technicians",
    summary:
      "A Pueblo electrical contractor stabilized lead flow with full-service digital marketing — fully booking the calendar and adding two technicians within the year.",
    problem:
      "A Pueblo electrical contractor had unpredictable lead flow — great months followed by dead ones. They couldn't confidently hire because they couldn't forecast pipeline. Their Google Business Profile was barely optimized and their site had no local SEO foundation.",
    approach: [
      "Optimized the Google Business Profile end-to-end (categories, services, photos, posts, Q&A).",
      "Built local SEO around Pueblo, Pueblo West, Cañon City, and Colorado City service terms.",
      "Added structured service pages for residential, commercial, panel upgrades, and EV charging.",
      "Implemented review generation tied to invoicing — building durable local trust.",
      "Set up a lead-flow dashboard the owner used to confidently hire two more techs.",
    ],
    results: [
      { label: "Calendar utilization", value: "Fully booked", context: "through busy season" },
      { label: "Technicians added", value: "+2", context: "due to sustained lead flow" },
      { label: "GBP performance", value: "Major lift", context: "calls + direction requests" },
      { label: "Lead consistency", value: "Predictable monthly pipeline", context: "vs. boom/bust before" },
    ],
    quote: {
      text: "Booked solid through the busy season. We've had to hire two more techs to keep up with the leads.",
      attribution: "Owner, Pueblo electrical contractor",
    },
    datePublished: "2025-04-12",
  },
  {
    slug: "cleaning-fort-collins",
    industry: "Cleaning",
    location: "Fort Collins",
    service: ["Local SEO", "Google Maps", "Social Media Marketing"],
    challenge:
      "Competing against national franchise brands with no marketing budget.",
    heroResult: "First-page for 12 Fort Collins keywords in 4 months",
    cardQuote:
      "They get small business. They get Colorado. And they get results. Couldn't recommend them more.",
    headline:
      "Cleaning Company in Fort Collins — First Page for 12 Keywords in 4 Months",
    summary:
      "A Fort Collins cleaning company outranked national franchise brands on a small-business budget — landing on page 1 for 12 high-intent local keywords in four months.",
    problem:
      "A small Fort Collins cleaning company was being crushed on Google by national franchise brands with massive ad budgets. They couldn't outspend them, had no clear local SEO, and their social presence was inconsistent.",
    approach: [
      "Built a focused local SEO strategy targeting neighborhood-level Fort Collins searches.",
      "Optimized the Google Business Profile and rebuilt service categories around real customer intent.",
      "Created keyword-targeted pages for residential, move-out, Airbnb, and post-construction cleaning.",
      "Set up a consistent, brand-aligned social media cadence focused on Fort Collins community trust.",
      "Built a steady review acquisition system — local proof the franchises couldn't replicate.",
    ],
    results: [
      { label: "First-page keywords", value: "12", context: "in 4 months" },
      { label: "Google Maps visibility", value: "Top 3", context: "for core Fort Collins cleaning terms" },
      { label: "New booking inquiries", value: "Major increase", context: "vs. pre-engagement baseline" },
      { label: "Ad spend required", value: "Minimal", context: "growth came from organic + maps" },
    ],
    quote: {
      text: "They get small business. They get Colorado. And they get results. Couldn't recommend them more.",
      attribution: "Owner, Fort Collins cleaning company",
    },
    datePublished: "2025-04-25",
  },
  {
    slug: "roofing-falcon",
    industry: "Roofing",
    location: "Falcon",
    service: ["Web Design", "SEO", "GEO / AI Search"],
    challenge:
      "Cookie-cutter template site, invisible in Falcon Map Pack, leads inconsistent.",
    heroResult: "Booked solid — hiring crews to keep up",
    cardQuote:
      "We moved off a cookie-cutter site, they wired SEO and AI search in from day one, and now we can't hire roofers fast enough.",
    headline:
      "Roofing Company in Falcon — Booked Solid, Hiring Crews to Keep Up",
    summary:
      "A Falcon roofing company replaced a cookie-cutter template site with a hand-built site wired for SEO, local Map Pack, and AI search — and went from inconsistent leads to a booked calendar and a hiring problem.",
    problem:
      "A Falcon roofing company was running on a templated site that looked like every other roofer in east El Paso County. They had real reviews, real experience, and real crews — but Google and AI engines couldn't tell them apart from the noise. Lead flow was unpredictable, the Map Pack belonged to Springs and Monument competitors, and PCS-cycle new-build owners in Meridian Ranch and Woodmen Hills were finding everyone else first.",
    approach: [
      "Replaced the cookie-cutter template with a hand-coded, fast, mobile-first site built around how Falcon homeowners actually search.",
      "Wired SEO, local Map Pack optimization, and GEO / AI search into the build from day one — not bolted on after.",
      "Built subdivision-level service pages for Meridian Ranch, Woodmen Hills, Falcon Highlands, Latigo Trails, and Peyton.",
      "Rebuilt the Google Business Profile with proper categories, service-area polygons, fresh project photos, and review velocity.",
      "Added hail-storm response landing pages, FAQ schema for insurance-claim timelines, and Q&A content the AI engines actually quote.",
    ],
    results: [
      { label: "Lead flow", value: "Booked solid", context: "vs. inconsistent before" },
      { label: "Crew hiring", value: "Actively hiring", context: "to keep up with demand" },
      { label: "Map Pack — Falcon roofing", value: "Top 3", context: "from off-page invisible" },
      { label: "AI search citations", value: "Quoted by ChatGPT + Perplexity", context: "for Falcon / Meridian Ranch roofing queries" },
    ],
    quote: {
      text: "We moved off a cookie-cutter site, they wired SEO and AI search in from day one, and now we can't hire roofers fast enough.",
      attribution: "Owner, Falcon roofing company",
    },
    datePublished: "2025-05-14",
  },
  {
    slug: "dental-woodland-park",
    industry: "Dental",
    location: "Woodland Park",
    service: ["Web Design", "Local SEO", "GEO / AI Search"],
    challenge:
      "New-patient pipeline outgrew the schedule — and the old site couldn't keep up.",
    heroResult: "New-patient pipeline outgrew the schedule",
    cardQuote:
      "Our new-patient calendar fills weeks out now. The phone rings from people who say ChatGPT recommended us — that never happened before.",
    headline:
      "Dental Practice in Woodland Park — New-Patient Pipeline Outgrew the Schedule",
    summary:
      "A Woodland Park dental practice replaced a generic template site with a hand-built, AI-search-optimized site — and grew the new-patient pipeline faster than the schedule could absorb.",
    problem:
      "A Woodland Park dental practice was running on a templated dental-vendor site. It looked fine but it didn't rank, it didn't differentiate them from Springs practices, and it had zero presence in AI answer engines. New-patient flow depended entirely on word of mouth, and a real percentage of Teller County and west-Springs patients were defaulting to whoever Google or ChatGPT named first.",
    approach: [
      "Hand-coded a fast, mobile-first site with real photos of the practice, the team, and Woodland Park — not stock dental imagery.",
      "Built service pages for the high-intent terms (Invisalign, implants, emergency dentist, cosmetic, family dental) tied to Woodland Park, Divide, Florissant, and west Colorado Springs.",
      "Wired in GEO / AI search optimization — quotable Q&A blocks, structured FAQ schema, and entity-clean LocalBusiness + Dentist graph.",
      "Rebuilt the Google Business Profile with correct categories, service-area polygons up Highway 24, fresh photos, and a steady review cadence tied to appointment completion.",
      "Added content for the predictable Teller County demand patterns — relocations from the Front Range, retirees moving up the mountain, and Pikes Peak-region tourists needing emergency care.",
    ],
    results: [
      { label: "New-patient pipeline", value: "Outgrew the schedule", context: "calendar booked weeks out" },
      { label: "AI engine citations", value: "Named in ChatGPT + Perplexity", context: "for Woodland Park dentist queries" },
      { label: "Map Pack — Woodland Park dental", value: "Top 3", context: "with steady review velocity" },
      { label: "West-Springs spillover", value: "New corridor of patients", context: "from Hwy 24 and west COS" },
    ],
    quote: {
      text: "Our new-patient calendar fills weeks out now. The phone rings from people who say ChatGPT recommended us — that never happened before.",
      attribution: "Practice owner, Woodland Park dental practice",
    },
    datePublished: "2025-06-08",
  },
  {
    slug: "moving-colorado-springs",
    industry: "Moving Company",
    location: "Colorado Springs",
    service: ["Web Design", "Google Maps", "LSA / Google Ads", "Social Media", "GEO / AI Search"],
    challenge:
      "Burned by a prior agency — Wix site that never ranked and a suspended Google Business Profile heading into PCS season.",
    heroResult: "Booked 3 weeks out through PCS season",
    cardQuote:
      "We'd been burned by two agencies before. The new site actually ranks, the phone rings, and we're turning jobs away during PCS.",
    headline:
      "Moving Company in Colorado Springs — Rescued From a Failed Agency Build, Booked 3 Weeks Out",
    summary:
      "A Colorado Springs moving company came to us after two agencies left them with a Wix site that never ranked and a Google Business Profile that had been suspended. We rebuilt the entire stack — site, Maps, LSA, social, and AI search — in time for PCS season.",
    problem:
      "The owner had been burned twice. The previous agency built a Wix site on a generic template, charged a monthly retainer for 'SEO' that was just keyword stuffing, and got the Google Business Profile suspended for using a virtual address. They had no Local Services Ads presence, no review system, and no visibility in AI answer engines — right as the spring PCS season was about to hit Fort Carson, Peterson, and Schriever.",
    approach: [
      "Worked directly with the owner of our shop (no salespeople, no account-manager handoffs) on a hand-coded, mobile-first website to replace the Wix build — with real photos of crews, trucks, and Colorado Springs jobs.",
      "Reinstated and rebuilt the Google Business Profile from scratch — verified address, correct categories, service-area polygons across El Paso County.",
      "Enrolled in Local Services Ads and earned the Google Guaranteed badge — top-of-results placement for 'movers near me' queries.",
      "Launched Google Ads with call-only campaigns timed to PCS-season search spikes (April–August).",
      "Built dedicated military-PCS landing pages for Fort Carson, Peterson SFB, Schriever SFB, and USAFA with GTCC and DITY-move guidance.",
      "Wired in GEO / AI search optimization — quotable Q&A blocks, FAQ schema, and entity-clean LocalBusiness graph so ChatGPT and Perplexity cite them for 'best movers in Colorado Springs.'",
      "Set up Instagram and Facebook Reels from real job sites — local-search engagement signals plus organic referral traffic.",
    ],
    results: [
      { label: "Calendar", value: "Booked 3+ weeks out", context: "through entire PCS season" },
      { label: "Map Pack — Springs movers", value: "Top 3", context: "after GBP reinstatement" },
      { label: "LSA leads", value: "Google Guaranteed live", context: "top-of-page placement" },
      { label: "AI engine citations", value: "Named in ChatGPT + Perplexity", context: "for Colorado Springs moving queries" },
    ],
    quote: {
      text: "We'd been burned by two agencies before. The new site actually ranks, the phone rings, and we're turning jobs away during PCS.",
      attribution: "Owner, Colorado Springs moving company",
    },
    datePublished: "2025-07-22",
  },
  {
    slug: "concrete-colorado-springs",
    industry: "Concrete & Flatwork",
    location: "Colorado Springs",
    service: ["Web Design", "Google Maps", "Google Ads", "Social Media", "GEO / AI Search"],
    challenge:
      "Sold by a slick closer at a national agency, then handed off to a rotating cast of account managers — $2K/month for 18 months, no clear answers, almost no tracked leads.",
    heroResult: "Cost-per-lead cut ~70%, booked 6+ weeks out",
    cardQuote:
      "We were just another invoice over there. Now I text the owner directly and actually know what's getting done that week.",
    headline:
      "Concrete Contractor in Colorado Springs — Cost-Per-Lead Cut ~70% After Leaving a National Agency",
    summary:
      "A Front Range concrete and flatwork contractor was paying a national 'contractor marketing' agency $2K/month and couldn't get a straight answer from anyone — the salesperson who sold them disappeared, and the account manager changed three times. We replaced the entire stack, kept pricing flat and published, and cut cost-per-lead by roughly 70%.",
    problem:
      "The owner got a polished sales pitch from a national contractor-marketing agency. The closer promised the world, then handed the account off to a junior AM — who got replaced twice in 18 months. Calls and emails went days without replies. They were paying $2K/month for a slow template site, miscategorized Google Business Profile, no call tracking, and no clarity on what was actually being done. Meanwhile, competitors with worse work were taking every driveway and patio job in the Springs and Monument corridor.",
    approach: [
      "Owner-to-owner relationship from day one — Chris (our owner) does the strategy, builds the site, and answers the phone. No sales reps, no handoffs, flat published pricing.",
      "Hand-coded a new website built for concrete buyers — real project galleries, before/after sliders, and dedicated service pages for driveways, patios, stamped concrete, retaining walls, and excavation.",
      "Recategorized the Google Business Profile (it had been set to 'general contractor' instead of 'concrete contractor'), added geo-tagged jobsite photos, and built a steady review cadence.",
      "Launched Google Ads with call-only campaigns and tightly geo-targeted Search campaigns for high-intent terms.",
      "Wired in GEO / AI search optimization — quotable Q&A around 'how much does a concrete driveway cost in Colorado Springs,' FAQ schema, and citation-friendly content that Perplexity and ChatGPT pull from.",
      "Set up weekly social proof posts — finished jobs, crew on site, time-lapse pours — across Instagram and Facebook.",
      "Implemented real call tracking and form tracking so every lead source was attributable for the first time.",
    ],
    results: [
      { label: "Cost-per-lead", value: "Down ~70%", context: "vs. prior agency spend" },
      { label: "Calendar", value: "Booked 6+ weeks out", context: "through pour season" },
      { label: "Map Pack — Springs concrete", value: "Top 3", context: "after recategorization" },
      { label: "AI engine citations", value: "Cited in Perplexity + ChatGPT", context: "for Colorado Springs concrete queries" },
    ],
    quote: {
      text: "We were just another invoice over there. Now I text the owner directly and actually know what's getting done that week.",
      attribution: "Owner, Colorado Springs concrete contractor",
    },
    datePublished: "2025-08-19",
  },
  {
    slug: "auto-repair-colorado-springs",
    industry: "Auto Repair",
    location: "Colorado Springs",
    service: ["Web Design", "Google Maps", "LSA / Google Ads", "Social Media", "GEO / AI Search"],
    challenge:
      "Paying a national auto-marketing vendor $3K+/month, couldn't get anyone on the phone, and felt like just another account number on the invoice.",
    heroResult: "Map Pack top-3 + leading review velocity",
    cardQuote:
      "With the last vendor I was account #4,000-something. Here I work with the owner — I get a real person, a real plan, and a price I can afford.",
    headline:
      "Independent Auto Repair Shop in Colorado Springs — Out of a National Vendor, Into the Map Pack",
    summary:
      "An independent auto repair shop in Colorado Springs was paying a national automotive-marketing vendor over $3K/month and couldn't get anyone to return a call. We moved them to an owner-led, Colorado-based program at a fraction of the price and rebuilt every channel.",
    problem:
      "The shop had been with a national automotive-marketing vendor for years — slick onboarding, then radio silence. They were one of thousands of accounts, support tickets went unanswered for days, and the monthly invoice kept climbing. They didn't own their website (it lived on the vendor's proprietary CMS), had no review-request system, were invisible in the Map Pack against dealership service centers, and had zero presence in AI answer engines for common repair questions.",
    approach: [
      "Direct line to the owner of our shop — no sales team, no offshore support queue, no rotating account managers. One Colorado-based person on the account, start to finish, at a flat published rate.",
      "Migrated off the proprietary CMS to a hand-coded, fully owned website — the shop now owns the domain, the code, and the content.",
      "Rebuilt the Google Business Profile with correct service categories, vehicle-type attributes (domestic, import, diesel, hybrid), and real shop photos.",
      "Set up automated post-repair-order review requests via SMS — review count and velocity now lead the local independent market.",
      "Enrolled in Local Services Ads to compete with dealership service centers for top-of-results placement.",
      "Wired in GEO / AI search optimization — FAQ schema for common repair questions ('why is my check engine light on,' 'how often should I change brakes in Colorado'), AnswerBlocks, and entity-clean AutoRepair graph.",
      "Launched short-form social content (Reels and TikTok) showing real shop work — earns trust and feeds local-search engagement signals.",
    ],
    results: [
      { label: "Site ownership", value: "Fully owned", context: "off the previous agency's proprietary CMS" },
      { label: "Map Pack — Springs auto repair", value: "Top 3", context: "vs. dealership service centers" },
      { label: "Review velocity", value: "Market-leading", context: "among Springs independents" },
      { label: "AI engine citations", value: "Named in ChatGPT + Perplexity", context: "for common repair queries" },
    ],
    quote: {
      text: "With the last vendor I was account #4,000-something. Here I work with the owner — I get a real person, a real plan, and a price I can afford.",
      attribution: "Owner, Colorado Springs auto repair shop",
    },
    datePublished: "2025-09-30",
  },
];

export const CASE_STUDY_SLUGS = CASE_STUDIES.map((c) => c.slug);

export function getCaseStudy(slug: string): CaseStudy | undefined {
  return CASE_STUDIES.find((c) => c.slug === slug);
}
